<template>
	<div>
		<el-select
			v-model="primarySelected"
			filterable
			clearable
			:class="{ 'invalid-supplier-select': isSupplierDeleted }"
			:disabled="isDisabled || creatingSupplier"
			@change="onPrimaryChange">
			<el-option
				v-for="item in filteredSuppliers"
				:key="item.supplierId"
				:label="item.name"
				:value="item.supplierId">
				<span>{{ item.name }}</span>
				<span class="supplier-type">{{ item.type }}</span>
			</el-option>
			<el-option
				key="SMARTKEYWORD_MARKETPLACE"
				label="SMARTKEYWORD_MARKETPLACE"
				value="SMARTKEYWORD_MARKETPLACE" />
			<el-option
				v-if="isSupplierDeleted"
				key="DELETED_SUPPLIER"
				:label="$t('deleted_supplier')"
				:value="value"
				disabled>
				<span class="deleted-supplier-label">{{ $t('deleted_supplier') }}</span>
			</el-option>
		</el-select>

		<el-select
			v-if="primarySelected === 'SMARTKEYWORD_MARKETPLACE'"
			v-model="secondarySelected"
			filterable
			clearable
			:disabled="creatingSupplier"
			class="secondary-select"
			@change="onSecondaryChange">
			<!-- created keeps this option visible while searching (element-ui internal flag, repurposed here, not its public allow-create prop) -->
			<el-option
				key="CREATE_NEW_INTERMEDIARY"
				:label="$t('create_intermediary_supplier')"
				:created="true"
				value="CREATE_NEW_INTERMEDIARY" />
			<el-option
				v-if="pendingSupplier"
				key="PENDING_NEW_INTERMEDIARY"
				:label="`${pendingSupplier.name} (${$t('pending_creation')})`"
				:created="true"
				value="PENDING_NEW_INTERMEDIARY" />
			<el-option
				v-for="item in intermediarySuppliers"
				:key="item.supplierId"
				:label="intermediaryLabel(item)"
				:value="item.supplierId" />
		</el-select>

		<div
			v-if="isDefaultMarketplaceSupplier"
			class="default-supplier-warning">
			{{ $t("default_supplier_warning") }}
		</div>

		<create-intermediary-supplier-dialog
			v-model="showCreateDialog"
			:creating="creatingSupplier"
			:initial-supplier="pendingSupplier"
			@create="onCreateSupplier" />
	</div>
</template>

<script>
import CreateIntermediarySupplierDialog from "@/pages/CampaignManager/FormItems/CreateIntermediarySupplierDialog";
import { normalizeSiteSource } from "@/javascripts/normalizeSiteSource.mjs";
import { contactEmails } from "@/javascripts/contactEmails.mjs";

export default {
	components: {
		CreateIntermediarySupplierDialog
	},
	props: {
		value: {
			type: String,
			default: null
		},
		sourceDomain: {
			type: String,
			default: null
		},
		supplierId: {
			type: String,
			default: null
		}
	},
	data() {
		return {
			allSuppliers: [],
			primarySelected: null,
			secondarySelected: null,
			showCreateDialog: false,
			creatingSupplier: false,
			pendingSupplier: null,
			suppliersLoaded: false
		};
	},
	computed: {
		isSupplierDeleted() {
			if (!this.suppliersLoaded || !this.value || this.value === "SMARTKEYWORD_MARKETPLACE") {
				return false;
			}
			return !this.allSuppliers.some((supplier) => supplier.supplierId === this.value);
		},
		// Supplier is mandatory: an empty selection is invalid too, unless the user
		// is mid-flow picking/creating a marketplace intermediary supplier.
		isSupplierMissing() {
			if (!this.suppliersLoaded || this.pendingSupplier || this.primarySelected === "SMARTKEYWORD_MARKETPLACE") {
				return false;
			}
			return !this.value;
		},
		hasNoValidSupplier() {
			return this.isSupplierDeleted || this.isSupplierMissing;
		},
		intermediarySuppliers() {
			const sourceUrl = normalizeSiteSource(this.sourceDomain);
			return this.allSuppliers.filter((supplier) => {
				const isIntermediary = supplier.type?.toUpperCase() === "INTERMEDIARY";
				const hasDomain = !this.sourceDomain || (supplier.links || []).some((link) => normalizeSiteSource(link.domain) === sourceUrl);
				const isSelected = supplier.supplierId === this.supplierId;
				return isIntermediary && (hasDomain || isSelected);
			});
		},
		isDisabled() {
			return !this.sourceDomain || (!this.filteredSuppliers.length && !this.intermediarySuppliers.length);
		},
		isDefaultMarketplaceSupplier() {
			if (!this.secondarySelected) {
				return false;
			}
			const supplier = this.intermediarySuppliers.find((supplier) => supplier.supplierId === this.secondarySelected);
			return supplier?.name?.toLowerCase() === "smartkeyword_marketplace";
		},
		filteredSuppliers() {
			const sourceUrl = normalizeSiteSource(this.sourceDomain);
			return this.allSuppliers.filter((supplier) => {
				const isIntermediary = supplier.type?.toUpperCase() === "INTERMEDIARY";
				const isPlatform = supplier.type?.toUpperCase() === "PLATFORM";
				const hasDomain = !this.sourceDomain || (supplier.links || []).some((link) => normalizeSiteSource(link.domain) === sourceUrl);
				const isSelected = supplier.supplierId === this.supplierId;
				const isNotMarketplace = supplier.name !== "smartkeyword_marketplace"

				return isNotMarketplace && !isIntermediary && (isPlatform || hasDomain || isSelected);
			});
		}
	},
	watch: {
		value(value) {
			this.initSelection(value);
		},
		allSuppliers() {
			this.initSelection(this.value);
		},
		pendingSupplier(value) {
			this.$emit("pending-supplier-change", Boolean(value));
		},
		hasNoValidSupplier: {
			immediate: true,
			handler(value) {
				this.$emit("invalid-supplier-change", value);
			}
		}
	},
	mounted() {
		this.fetchSuppliers();
	},
	methods: {
		initSelection(value) {
			if (!value) {
				if (this.primarySelected !== "SMARTKEYWORD_MARKETPLACE" && !this.pendingSupplier) {
					this.primarySelected = null;
					this.secondarySelected = null;
				}
				return;
			}
			const isIntermediary = this.intermediarySuppliers.some((supplier) => supplier.supplierId === value);
			if (isIntermediary) {
				this.primarySelected = "SMARTKEYWORD_MARKETPLACE";
				this.secondarySelected = value;
			} else {
				this.primarySelected = value;
				this.secondarySelected = null;
			}
		},
		// The select shows one line, so a supplier's emails are comma separated, then its name.
		intermediaryLabel(supplier) {
			const emails = contactEmails(supplier.contactEmail);
			return emails.length ? `${emails.join(", ")} (${supplier.name})` : supplier.name;
		},
		onPrimaryChange(value) {
			this.pendingSupplier = null;
			if (value !== "SMARTKEYWORD_MARKETPLACE") {
				this.secondarySelected = null;
				this.$emit("input", value);
			} else {
				this.$emit("input", null);
			}
		},
		onSecondaryChange(value) {
			if (value === "CREATE_NEW_INTERMEDIARY") {
				this.secondarySelected = this.pendingSupplier ? "PENDING_NEW_INTERMEDIARY" : this.value;
				this.showCreateDialog = true;
				return;
			}
			this.pendingSupplier = null;
			this.$emit("input", value);
		},
		onCreateSupplier(supplier) {
			this.pendingSupplier = supplier;
			// el-select caches the displayed label when `value` changes; re-editing the
			// pending supplier keeps the same sentinel value, so force a cycle to make
			// it re-read the (now updated) option label instead of showing the stale one.
			this.secondarySelected = null;
			this.$nextTick(() => {
				this.secondarySelected = "PENDING_NEW_INTERMEDIARY";
			});
			this.showCreateDialog = false;
		},
		// Called by the parent form right before it submits, so the supplier is only
		// actually created once the whole backlink is validated (avoids orphan suppliers).
		commitPendingSupplier() {
			if (!this.pendingSupplier) {
				return Promise.resolve(this.value);
			}
			this.creatingSupplier = true;
			const { name, contactEmail } = this.pendingSupplier;
			return this.$apiV2
				.post(
					"/netlinking-sourcing/suppliers",
					{
						data: {
							type: "CREATE_OR_UPDATE_NETLINKING_SUPPLIERS",
							attributes: {
								// Joined with commas: the current API reads a string, the array version splits it.
								suppliers: [{ name, type: "intermediary", contactEmail: contactEmail.join(",") || null }]
							}
						}
					},
					{ headers: { "Content-Type": "application/vnd.api+json" } }
				)
				.then((response) => {
					const created = response.data.data.attributes.suppliers[0];
					this.allSuppliers.push(created);
					this.pendingSupplier = null;
					this.secondarySelected = created.supplierId;
					this.$emit("input", created.supplierId);
					return created.supplierId;
				})
				.catch((err) => {
					console.error(err);
					throw err;
				})
				.finally(() => {
					this.creatingSupplier = false;
				});
		},
		fetchSuppliers() {
			this.fetchPending = true;
			this.$apiV2
				.get("/netlinking-sourcing/suppliers")
				.then((response) => {
					this.allSuppliers = response.data.data.attributes;
				})
				.catch((err) => console.error(err))
				.finally(() => {
					this.suppliersLoaded = true;
				});
		}
	}
};
</script>

<i18n>
{
  "en": {
    "default_supplier_warning": "Default value: missing data for this supplier.",
    "create_intermediary_supplier": "+ Create an intermediary supplier",
    "pending_creation": "will be created on save",
    "deleted_supplier": "Supplier not found"
  },
  "fr": {
    "default_supplier_warning": "Valeur par défaut : données manquantes pour ce fournisseur.",
    "create_intermediary_supplier": "+ Créer un fournisseur intermédiaire",
    "pending_creation": "sera créé à l'enregistrement",
    "deleted_supplier": "Fournisseur introuvable"
  },
  "de": {
    "default_supplier_warning": "Standardwert: fehlende Daten für diesen Anbieter.",
    "create_intermediary_supplier": "+ Vermittler-Anbieter erstellen",
    "pending_creation": "wird beim Speichern erstellt",
    "deleted_supplier": "Anbieter nicht gefunden"
  }
}
</i18n>

<style scoped>
.secondary-select {
	margin-top: 8px;
}

.supplier-type {
	float: right;
	color: #8492a6;
	font-size: 12px;
	margin-left: 16px;
}

.default-supplier-warning {
	margin-top: 4px;
	color: #e6a23c;
	font-size: 12px;
}

.deleted-supplier-label {
	color: #f56c6c;
}

.invalid-supplier-select ::v-deep .el-input__inner {
	color: #f56c6c;
}
</style>
